import { CookieService } from 'ngx-cookie-service';
import { Injectable, inject } from '@angular/core';
import { createStore } from '@ngneat/elf';
import { selectAllEntities, setEntities, withEntities } from '@ngneat/elf-entities';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

import { ID } from '@state/base';
import { ClientNotification, DISMISSED_COOKIE_NAME } from './client-notifications.model';

const initialValue = [
  // Add notifications here as needed, e.g.:
  // {
  //   id: [UUID],
  //   message: 'Example notification message with <a href="https://example.com">a link</a>.' }
  // }
];

@Injectable({ providedIn: 'root' })
export class ClientNotificationsRepository {
  private cookieService = inject(CookieService);
  private _store$ = createStore(
    { name: 'client-notifications' },
    withEntities<ClientNotification>({ initialValue })
  );
  private _dismissedIds$ = new BehaviorSubject<ID[]>(this.getDismissedIds());

  clientNotifications$ = this._store$.pipe(selectAllEntities());

  activeClientNotifications$ = combineLatest([this.clientNotifications$, this._dismissedIds$]).pipe(
    map(([notifications, dismissedIds]) => notifications.filter((n) => !dismissedIds.includes(n.id)))
  );

  hasActiveClientNotifications$ = this.activeClientNotifications$.pipe(
    map((notifications) => notifications.length > 0)
  );

  getClientNotifications(): ClientNotification[] {
    return Object.values(this._store$.getValue().entities);
  }

  getDismissedIds(): ID[] {
    let cookie = this.cookieService.get(DISMISSED_COOKIE_NAME);

    if (!cookie) return [];

    return cookie.split(',').filter((id) => id.trim() !== '');
  }

  setDismissedIds(ids: ID[]): void {
    this._dismissedIds$.next(ids);
  }

  setClientNotifications(notifications: ClientNotification[]): void {
    this._store$.update(setEntities(notifications));
  }

  reset(): void {
    this._store$.reset();
  }
}
